import { Shareholder } from '../models/shareholder.interface';
import { Contacts, Entity, OnboardingDomains } from '../models/entity.model';
import { ShareholdersQueries } from '../store/shareholders/shareholders.queries';
import { first, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { ENTITY_TYPE } from '../constants/enum.const';
import { Country } from '../models/country.interface';
import { ArrayUtils } from './array.util';
import { StaticData } from '../constants/static.const';

@Injectable()
export class EntityUtils {
	private allCountries: Array<Country> = ArrayUtils.sortObject(
		StaticData.countries,
		'name'
	);

	constructor(private readonly shareholdersQueries?: ShareholdersQueries) {}

	private _questionKeys: Record<string, string> = {
		// sub-individual
		first_name: 'fc603e30-de3b-4e5d-9f58-78b45c82d2ca',
		last_name: '1a10b4bd-7ac9-4d4e-8119-be299f8e77c4',
		email: '8d593f91-780e-401c-a055-bfc0a6e9e22e',
		mobile: 'a7018dcc-0cc4-4f8e-8bbd-46f13d4e8ce7',
		dob: '9ae32e57-321c-4e1d-9bdf-19f2d27d117e',
		dobConfirmation: '0ae45474-9b43-4f52-acb8-0b52f38b9cd9',
		nationality: '0bc38ce4-49a7-45b9-a5ac-57aba9465954',
		job_title: 'a99e4c6c-89fe-4dea-a5dc-d814601d517f',
		legal_person_type: '578fe3dd-f433-4622-a324-68b4e0182261',

		// sub-corporate
		entity_name: 'fa6c5d84-276f-4a5f-b9a6-a2e44d26b48b',
		company_registration_number: '21fe315f-2e3d-403f-8a2b-76065aeef4b5',
		date_of_incorporation: '1126f63c-f1a7-477a-a2b8-58582ae37494',
		date_of_incorporation_reason: 'ea885c5f-5d26-43cc-be27-7d95d1d9a54b',
		country_of_incorporation: '193b4d5f-7ad8-4c68-8db1-37094ca85e29',
		company_is_holding: 'aed5ab6c-2a83-44bd-8a51-23ba53215ea2',
		company_is_fund: '37c3805a-ee5e-46e1-a10d-095b238a4cdc',
		company_is_trust: '128e3240-2bfe-40ac-8461-017b50b8eae1',

		// address
		street: '84b91e75-ffc5-48b7-a30a-c815ab7a1add',
		sub_street: 'b72642d8-2e9d-4a99-b035-66502da09e03',
		city: '8b1e2efc-420f-41cb-a4a4-bcf5117f71e8',
		state: '1c833f6a-a076-4de0-9093-d36367f3014c',
		us_state: '7e76524b-6041-4bed-a4d5-efd54cd036ed',
		postcode: '52f8618b-f20d-46b0-9365-a9800b189d8a',
		country: 'b00749cb-c817-4065-a5c1-351d1f5de45a',

		// operating / physical address (corporate only, PLAT-706)
		operating_heading: 'e38f4c81-6f6e-4433-b9a2-66927e4aa053',
		same_as_registered: '85517b76-e770-40dc-83d6-1146d146d608',
		operating_street: '6d67bce7-a3ca-435b-bd89-ed08e4c49b6b',
		operating_sub_street: '6e97f2a0-609e-4df3-8b90-b5367fdce760',
		operating_city: '0b3f585a-293a-4ad5-8c86-aa79a156d60b',
		operating_state: '9674edd4-1d13-48d9-9eea-f6268f333868',
		operating_us_state: 'd920de8a-ccff-46fc-9da0-7ddd12f23ab9',
		operating_postcode: '2c9792e3-6117-4081-a1a8-e337fbb2d0ff',
		operating_country: '6c029180-2d75-4c9b-ba02-caa13b87688c',

		// corporate only
		authorisation: 'cea098bd-6131-466c-8db2-707e686ae770',
		position: 'dacf60b6-c9a7-4e34-baa5-7e6f361f15ec',
		invoice_email: 'c0d5439c-fe4d-4e6a-88bb-4dacf427d270',

		// individual only
		gender: '62f8d7a0-6f70-416d-ab62-0ec8de15c247',

		// both
		shares: '04cf954f-aeb7-419f-99a0-71bc511a7d24',
		tax_number: 'cbd23cc7-17cd-408a-b2d7-cd8fbcd1e1ef',
		ssn_itin: 'f031b701-12d4-4418-9a7b-2818b78f443b',

		// entityContacts
		fullName: '997de497-a6c4-4bf4-a4fe-ce1e396084d7',
	};

	get questionKeys(): Record<string, string> {
		return this._questionKeys;
	}

	async getRemainingSharesForParent(
		shareholder: Shareholder | Entity,
		field: 'id' | 'parent_id'
	): Promise<number> {
		if (!this.shareholdersQueries) {
			throw new Error(
				'Provide a [ShareholdersQueries] to the constructor'
			);
		}
		return await this.shareholdersQueries
			.getShareholdersByParent(shareholder[field] ?? undefined)
			.pipe(
				first(),
				map((data: Array<Shareholder>) => {
					return data.reduce((acc: number, item: Shareholder) => {
						return acc + Number(item.profile?.shares ?? 0);
					}, 0);
				}),
				map((data: number) => {
					switch (field) {
						case 'id':
							return 100 - data;
						case 'parent_id':
							return (
								100 -
								(data -
									Number(shareholder.profile?.shares ?? 0))
							);
					}
				})
			)
			.toPromise();
	}

	getRemainingSharesForShareholders(
		entity: Shareholder | Entity,
		shareholders: Array<Shareholder | Entity>,
		field: 'id' | 'parent_id',
		excessShares: number = 0
	): number {
		const totalShares: number = shareholders.reduce(
			(acc: number, item: Shareholder | Entity) => {
				return acc + Number(item.profile?.shares ?? 0);
			},
			-excessShares
		);

		switch (field) {
			case 'id':
				return 100 - totalShares;
			case 'parent_id':
				return (
					100 - (totalShares - Number(entity.profile?.shares ?? 0))
				);
		}
	}

	/**
	 * The nationalities given as the answer, which is a list now that the question accepts
	 * several. An application answered before it did holds a single country.
	 */
	private answeredNationalities(values: Record<string, any>): Array<Country> {
		const answer = values[this._questionKeys.nationality];

		return (Array.isArray(answer) ? answer : [answer]).filter(
			(country): country is Country => !!country
		);
	}

	/**
	 * Every nationality held, as the country objects the question stores them as.
	 *
	 * The question accepts several answers, so it reads back a list. An application answered
	 * before it did holds a single country in the `nationality` column alone.
	 */
	private nationalitiesOf(entity: Entity): Array<Country> | undefined {
		const codes = entity.nationalities?.length
			? entity.nationalities
			: [entity.nationality ?? entity.profile?.nationality];

		const countries = codes
			.map((code) =>
				this.allCountries.find(
					(country: Country) => country.code === code
				)
			)
			.filter((country): country is Country => !!country);

		return countries.length ? countries : undefined;
	}

	mapEntityToQuestions(entity: Entity): Record<string, any> {
		const isCorporateFlow =
			entity.type === ENTITY_TYPE.corporate ||
			entity.type === ENTITY_TYPE.sub_corporate;
		return {
			entityId: entity.id,
			[this._questionKeys.entity_name]: entity.entity_name,
			...(isCorporateFlow
				? {
						[this._questionKeys.company_registration_number]:
							entity.profile?.company_registration_number,
						[this._questionKeys.date_of_incorporation]:
							entity.profile?.incorporation_date,
						[this._questionKeys.country_of_incorporation]:
							this.allCountries.find(
								(country: Country) =>
									country.code ===
									entity.profile?.incorporation_country
							),
				  }
				: {}),
			[this._questionKeys.first_name]: entity.first_name,
			[this._questionKeys.last_name]: entity.last_name,
			[this._questionKeys.email]: entity.email,
			[this._questionKeys.mobile]: entity.mobile,
			[this._questionKeys.dob]: entity.profile?.dob,
			[this._questionKeys.dobConfirmation]: entity.profile?.dob,
			[this._questionKeys.gender]: entity.profile?.gender,
			[this._questionKeys.nationality]: this.nationalitiesOf(entity),
			[this._questionKeys.job_title]: entity.profile?.job_title,
			// Both questions read the same column, the question config decides which one is shown
			[this._questionKeys.tax_number]: entity.profile?.tax_number,
			[this._questionKeys.ssn_itin]: entity.profile?.tax_number,
			[this._questionKeys.legal_person_type]:
				entity.profile?.legal_person_type,
			[this._questionKeys.shares]: entity.profile?.shares,
			[this._questionKeys.street]: entity.address?.street,
			[this._questionKeys.sub_street]: entity.address?.sub_street,
			[this._questionKeys.city]: entity.address?.city,
			// Both questions read address.state, the question config decides which one is shown
			[this._questionKeys.state]: entity.address?.state,
			[this._questionKeys.us_state]: entity.address?.state
				? {
						label: entity.address.state,
						value: entity.address.state,
				  }
				: undefined,
			[this._questionKeys.postcode]: entity.address?.postcode,
			[this._questionKeys.country]: this.allCountries.find(
				(country: Country) => country.code === entity.address?.country
			),
		};
	}

	mapQuestionsToEntity(
		values: Record<string, any>,
		entityType: ENTITY_TYPE = ENTITY_TYPE.sub_individual
	): Entity {
		const entity = new Entity();
		entity.profile = {};
		entity.address = {};

		entity.first_name =
			values[this._questionKeys.first_name] ?? '--no-value--';
		entity.last_name =
			values[this._questionKeys.last_name] ?? '--no-value--';
		entity.email = values[this._questionKeys.email] ?? '--no-value--';
		entity.mobile = values[this._questionKeys.mobile];
		entity.entity_name = values[this._questionKeys.entity_name];
		entity.type = entityType;

		// Entity Profile
		entity.profile.dob = values[this._questionKeys.dob];
		// The column holds one nationality, so it takes the first of several, the same one the
		// server promotes into entities.nationality and carries into screening.
		entity.profile.nationality =
			this.answeredNationalities(values)[0]?.nationality;
		entity.profile.job_title = values[this._questionKeys.job_title];
		entity.profile.tax_number = this.readTaxNumberAnswer(values);
		entity.profile.legal_person_type = values[
			this._questionKeys.legal_person_type
		]?.map((item: any) => item.value);
		entity.profile.shares = values[this._questionKeys.shares];
		entity.profile.shareholder =
			values[this._questionKeys.shares] > 0 ||
			entity.profile.legal_person_type?.includes('shareholder');
		entity.profile.director =
			entity.profile.legal_person_type?.includes('director');
		entity.profile.company_is_fund =
			values[this._questionKeys.company_is_fund];
		if (
			entityType === ENTITY_TYPE.corporate ||
			entityType === ENTITY_TYPE.sub_corporate
		) {
			entity.profile.company_registration_number =
				values[this._questionKeys.company_registration_number];
			Object.assign(entity.profile, {
				incorporation_date:
					values[this._questionKeys.date_of_incorporation],
				incorporation_country:
					values[this._questionKeys.country_of_incorporation]?.code ??
					values[this._questionKeys.country_of_incorporation],
			});
		}
		entity.profile.company_is_trust =
			values[this._questionKeys.company_is_trust];
		entity.profile.company_is_holding =
			values[this._questionKeys.company_is_holding];
		entity.profile.gender = values[this._questionKeys.gender]?.value;

		// Entity Address
		entity.address.street = values[this._questionKeys.street];
		entity.address.sub_street = values[this._questionKeys.sub_street];
		entity.address.city = values[this._questionKeys.city];
		entity.address.state = this.readStateAnswer(values);
		entity.address.postcode = values[this._questionKeys.postcode];
		entity.address.country = values[this._questionKeys.country]?.code;

		if (entity.profile.shareholder) {
			if (values[this._questionKeys.shares] > 25) {
				entity.profile.legal_person_type?.push('ubo');
			}
		}
		return entity;
	}

	operatingAddressKeys(): string[] {
		const keys = this._questionKeys;
		return [
			keys.operating_street,
			keys.operating_sub_street,
			keys.operating_city,
			keys.operating_state,
			keys.operating_us_state,
			keys.operating_postcode,
			keys.operating_country,
		];
	}

	/**
	 * While "same as registered" is ticked, mirror the registered address into the
	 * operating controls and lock them. Unticking unlocks the operating controls
	 * and leaves the mirrored values in place for editing.
	 * Returns true when a locked operating value actually changed.
	 */
	syncOperatingAddressWithRegistered(
		formGroup: UntypedFormGroup,
		pageDisabled = false
	): boolean {
		const same = !!formGroup.get(this._questionKeys.same_as_registered)
			?.value;
		if (same) {
			const changed = this.copyOperatingAddressFromRegistered(formGroup);
			this.setOperatingAddressDisabled(formGroup, true);
			return changed;
		}

		if (!pageDisabled) {
			this.setOperatingAddressDisabled(formGroup, false);
		}
		return false;
	}

	/**
	 * When the client ticks "same as registered", copy registered address answers
	 * into the operating address controls before validation and save (PLAT-706).
	 * Country is copied first so operating state showWhen rules can react.
	 * Returns true when an operating value changed.
	 */
	copyOperatingAddressFromRegistered(formGroup: UntypedFormGroup): boolean {
		const keys = this._questionKeys;
		if (!formGroup.get(keys.same_as_registered)?.value) {
			return false;
		}

		const country = formGroup.get(keys.country)?.value;
		let changed = this.assignControl(
			formGroup.get(keys.operating_country),
			country
		);

		const fieldPairs: Array<[string, string]> = [
			[keys.street, keys.operating_street],
			[keys.sub_street, keys.operating_sub_street],
			[keys.city, keys.operating_city],
			[keys.postcode, keys.operating_postcode],
		];
		for (const [fromKey, toKey] of fieldPairs) {
			changed =
				this.assignControl(
					formGroup.get(toKey),
					formGroup.get(fromKey)?.value
				) || changed;
		}

		if (country?.code === 'US') {
			changed =
				this.assignControl(
					formGroup.get(keys.operating_us_state),
					formGroup.get(keys.us_state)?.value
				) || changed;
		} else {
			changed =
				this.assignControl(
					formGroup.get(keys.operating_state),
					formGroup.get(keys.state)?.value
				) || changed;
		}

		return changed;
	}

	private assignControl(
		control: AbstractControl | null,
		value: unknown
	): boolean {
		if (!control || this.valuesMatch(control.value, value)) {
			return false;
		}
		control.setValue(value);
		return true;
	}

	private setOperatingAddressDisabled(
		formGroup: UntypedFormGroup,
		disabled: boolean
	): void {
		for (const key of this.operatingAddressKeys()) {
			const control = formGroup.get(key);
			if (!control) {
				continue;
			}
			if (disabled && control.enabled) {
				control.disable({ emitEvent: false });
			}
			if (!disabled && control.disabled) {
				control.enable({ emitEvent: false });
			}
		}
	}

	private valuesMatch(left: unknown, right: unknown): boolean {
		return JSON.stringify(left) === JSON.stringify(right);
	}

	private readTaxNumberAnswer(
		values: Record<string, unknown>
	): string | undefined {
		// US legal persons answer SSN/ITIN, everyone else answers Tax/IN Number.
		const answer =
			values[this._questionKeys.ssn_itin] ??
			values[this._questionKeys.tax_number];

		return typeof answer === 'string' ? answer : undefined;
	}

	private readStateAnswer(
		values: Record<string, unknown>
	): string | undefined {
		const usState = values[this._questionKeys.us_state];
		const textState = values[this._questionKeys.state];
		const selected = usState ?? textState;

		if (selected && typeof selected === 'object' && 'value' in selected) {
			const code = (selected as { value?: unknown }).value;
			return typeof code === 'string' ? code : undefined;
		}

		return typeof selected === 'string' ? selected : undefined;
	}

	mapEntityContactToQuestions(contactDetails: Contacts): Record<string, any> {
		return {
			entityId: contactDetails.entityId,
			type: contactDetails.type,
			[this._questionKeys.fullName]: contactDetails?.name,
			[this._questionKeys.position]: contactDetails?.position,
			[this._questionKeys.email]: contactDetails?.email,
			[this._questionKeys.mobile]: contactDetails?.mobile,
		};
	}

	mapQuestionsToEntityContact(values: Record<string, any>): Contacts {
		const contact = new Contacts();

		contact.type = values.type;
		contact.entityId = values.entityId;
		contact.name = values[this._questionKeys.fullName];
		contact.position = values[this._questionKeys.position];
		contact.email = values[this._questionKeys.email];
		contact.mobile = values[this._questionKeys.mobile];

		return contact;
	}
}
